import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useState } from 'react';
import { updatePortalListing } from '../../services/api';

function getInitialAddressValue(listing, key) {
  return String(listing?.[key] || '').trim();
}

export function formatListingAddress({ addressLine1, addressLine2, city, state, postalCode }) {
  const street = [addressLine1, addressLine2].map((value) => String(value || '').trim()).filter(Boolean).join(', ');
  const locality = [
    String(city || '').trim(),
    [String(state || '').trim(), String(postalCode || '').trim()].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  return [street, locality].filter(Boolean).join(', ');
}

export default function ListingAddressEditor({ listing, onSaved, onCancel }) {
  const toast = useFeedback();
  const [values, setValues] = useState({
    addressLine1: getInitialAddressValue(listing, 'addressLine1') || getInitialAddressValue(listing, 'address'),
    addressLine2: getInitialAddressValue(listing, 'addressLine2'),
    city: getInitialAddressValue(listing, 'city'),
    state: getInitialAddressValue(listing, 'state'),
    postalCode: getInitialAddressValue(listing, 'postalCode'),
  });
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');



  const updateValue = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setValidationError('');


  };

  const validate = () => {
    if (!values.addressLine1.trim()) return 'Please enter a street address.';
    if (!values.city.trim()) return 'Please enter a city.';
    if (!values.state.trim()) return 'Please enter a state.';
    if (!values.postalCode.trim()) return 'Please enter a ZIP code.';
    return '';
  };

  const save = async (event) => {
    event.preventDefault();
    if (!listing?.id) return;

    const nextValidationError = validate();
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    const payload = {
      addressLine1: values.addressLine1.trim(),
      addressLine2: values.addressLine2.trim() || null,
      city: values.city.trim(),
      state: values.state.trim(),
      postalCode: values.postalCode.trim(),
    };

    try {
      setSaving(true);
      setValidationError('');


      const updatedListing = await updatePortalListing(listing.id, payload);
      toast.success('Address updated.', { id: 'action:ListingAddressEditor:save' });
      await onSaved?.({
        updatedListing,
        address: formatListingAddress(payload),
        values: payload,
      });
    } catch {
      toast.error('Unable to save address. Please try again.', { id: 'action:ListingAddressEditor:save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="flex min-h-[52vh] flex-col" onSubmit={save}>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-5">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-address-line-1">
            Street Address
          </label>
          <input
            id="listing-address-line-1"
            type="text"
            value={values.addressLine1}
            onChange={(event) => updateValue('addressLine1', event.target.value)}
            className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-address-line-2">
            Suite, Unit, or Floor
          </label>
          <input
            id="listing-address-line-2"
            type="text"
            value={values.addressLine2}
            onChange={(event) => updateValue('addressLine2', event.target.value)}
            className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_5.5rem_minmax(0,8rem)]">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-address-city">
              City
            </label>
            <input
              id="listing-address-city"
              type="text"
              value={values.city}
              onChange={(event) => updateValue('city', event.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-address-state">
              State
            </label>
            <input
              id="listing-address-state"
              type="text"
              value={values.state}
              onChange={(event) => updateValue('state', event.target.value)}
              maxLength={2}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm uppercase text-gray-950 shadow-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-address-postal-code">
              ZIP Code
            </label>
            <input
              id="listing-address-postal-code"
              type="text"
              value={values.postalCode}
              onChange={(event) => updateValue('postalCode', event.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
            />
          </div>
        </div>

        {validationError ? (
          <InlineFeedback variant="error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {validationError}
          </InlineFeedback>
        ) : null}


      </div>

      <div className="sticky bottom-0 -mx-5 border-t border-gray-200 bg-white px-5 py-4">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Address'}
          </button>
        </div>
      </div>
    </form>
  );
}
